import type { MedplumClient, WithId } from '@medplum/core';
import type {
  ActivityDefinition,
  Bundle,
  BundleEntry,
  PlanDefinition,
} from '@medplum/fhirtypes';

import { type PlanSpec, specToResources } from '@eluve/plan-shapes';

const PAGE_SIZE = 200;
// Belt-and-suspenders page cap: guarantees the `next`-link loop terminates
// even on a misbehaving server.
const MAX_PAGES = 50;

export function planDefinitionTitle(pd: PlanDefinition): string {
  return pd.title ?? pd.name ?? 'Untitled plan';
}

// Condition displays from PlanDefinition.topic.
export function planConditions(pd: PlanDefinition): string[] {
  return (pd.topic ?? [])
    .map((cc) => cc.text ?? cc.coding?.[0]?.display)
    .filter((t): t is string => Boolean(t));
}

// --- PlanDefinition list ---------------------------------------------------

// Pulls every active PlanDefinition in the tenant's project. We expect a small
// set (~a dozen protocols), so this fetches once and the list page filters in
// memory.
export async function fetchPlanDefinitions(
  client: MedplumClient,
): Promise<WithId<PlanDefinition>[]> {
  const results: WithId<PlanDefinition>[] = [];

  const qs = new URLSearchParams();
  qs.append('_count', String(PAGE_SIZE));
  qs.append('_sort', 'title');

  const ingest = (bundle: Bundle<PlanDefinition>) => {
    for (const entry of bundle.entry ?? []) {
      const r = entry.resource;
      if (r?.id) {
        results.push(r as WithId<PlanDefinition>);
      }
    }
    return bundle.link?.find((l) => l.relation === 'next')?.url;
  };

  let nextUrl = ingest(
    (await client.search(
      'PlanDefinition',
      qs.toString(),
    )) as Bundle<PlanDefinition>,
  );
  let pages = 1;
  while (nextUrl && pages < MAX_PAGES) {
    const bundle = (await client.get(nextUrl)) as Bundle<PlanDefinition>;
    nextUrl = ingest(bundle);
    pages += 1;
  }

  results.sort((a, b) =>
    planDefinitionTitle(a).localeCompare(planDefinitionTitle(b)),
  );
  return results;
}

// --- Save ---------------------------------------------------------------------

// The transaction Bundle Save posts: the PlanDefinition plus its plan-scoped
// ActivityDefinitions, all-or-nothing — a stored plan never references an AD
// that failed to insert.
export function planSaveBundle(spec: PlanSpec): Bundle {
  const { planDefinition, activityDefinitions } = specToResources(spec);
  const post = (
    resource: ActivityDefinition | PlanDefinition,
  ): BundleEntry => ({
    resource,
    request: { method: 'POST', url: resource.resourceType },
  });
  return {
    resourceType: 'Bundle',
    type: 'transaction',
    entry: [...activityDefinitions.map(post), post(planDefinition)],
  };
}
